import { IconAlertTriangle, IconLock, IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import SelectDropdown from '@/components/atoms/SelectDropdown';
import { useRestrictedFeature } from '@/hooks/useGlobalRestrictionDialog';
import useTranslationState from '@/hooks/useTranslationState';
import { UnsupportedLanguageWarningModal } from '@/components/molecules/UnsupportedLanguageWarningModal/UnsupportedLanguageWarningModal';

export const TranslationLanguageSelectDropdown = ({
  translatedLanguagesOptions,
  selectedLanguageName,
  onLanguageSelection,
  isTranslationProcessing,
  openCaptionTranslationModal,
  allowCaptionTranslation,
  handleCaptionTranslationRestricted,
  isOriginalLanguageSupportedForTranslation,
  checkLanguageAndExecute,
  isWarningModalOpen,
  closeWarningModal
}: Pick<
  ReturnType<typeof useTranslationState>,
  | 'translatedLanguagesOptions'
  | 'selectedLanguageName'
  | 'onLanguageSelection'
  | 'isTranslationProcessing'
  | 'allowCaptionTranslation'
  | 'handleCaptionTranslationRestricted'
  | 'isOriginalLanguageSupportedForTranslation'
  | 'checkLanguageAndExecute'
  | 'isWarningModalOpen'
  | 'closeWarningModal'
> & { openCaptionTranslationModal: () => void }) => {
  const { handleAction } = useRestrictedFeature(
    'captionTranslation',
    allowCaptionTranslation,
    () => checkLanguageAndExecute(openCaptionTranslationModal),
    {
      reason: 'plan',
      onSecondaryAction: handleCaptionTranslationRestricted
    }
  );

  const actionIcon = useMemo(() => {
    if (!allowCaptionTranslation) {
      return <IconLock size={16} className="text-slate-600" />;
    }

    if (!isOriginalLanguageSupportedForTranslation) {
      return <IconAlertTriangle size={16} className="text-amber-500" />;
    }

    return <IconPlus size={14} className="text-slate-600" />;
  }, [allowCaptionTranslation, isOriginalLanguageSupportedForTranslation]);

  return (
    <>
      <div className="min-w-[116px] max-w-[240px]">
        <SelectDropdown
          options={translatedLanguagesOptions}
          selected={selectedLanguageName}
          onChange={language => onLanguageSelection(language)}
          menuClassName="right-0 left-auto"
          placeholder="Select language"
          actionItem={{
            isDisabled: isTranslationProcessing,
            label: 'New translation',
            onClick: () => {
              handleAction();
            },
            icon: actionIcon
          }}
        />
      </div>

      <UnsupportedLanguageWarningModal isOpen={isWarningModalOpen} onClose={closeWarningModal} />
    </>
  );
};
